import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post("/auth/login", { email, password }).then((res) => res.data.data),

  register: (userData: any) =>
    api.post("/auth/register", userData).then((res) => res.data.data),

  getCurrentUser: (token: string) =>
    api
      .get("/auth/me", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.data.data.user),

  updateProfile: (data: any, token: string) =>
    api
      .put("/auth/profile", data, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => res.data.data.user),
};

// Dashboard API
export const dashboardAPI = {
  getDashboards: () =>
    api.get("/dashboards").then((res) => res.data.data.dashboards),

  getDashboard: (id: string) =>
    api.get(`/dashboards/${id}`).then((res) => res.data.data),

  createDashboard: (data: any) =>
    api.post("/dashboards", data).then((res) => res.data.data.dashboard),

  updateDashboard: (id: string, data: any) =>
    api.put(`/dashboards/${id}`, data).then((res) => res.data.data.dashboard),

  deleteDashboard: (id: string) =>
    api.delete(`/dashboards/${id}`).then((res) => res.data),

  duplicateDashboard: (id: string) =>
    api
      .post(`/dashboards/${id}/duplicate`)
      .then((res) => res.data.data.dashboard),

  getSharedDashboard: (token: string) =>
    api.get(`/dashboards/shared/${token}`).then((res) => res.data.data),
};

// Data Sources API
export const dataSourceAPI = {
  getDataSources: () =>
    api.get("/data-sources").then((res) => res.data.data.dataSources),

  getDataSource: (id: string) =>
    api.get(`/data-sources/${id}`).then((res) => res.data.data.dataSource),

  uploadCSV: (file: File, name: string) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);

    return api
      .post("/data-sources/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => res.data.data);
  },

  getDataSourceData: (id: string, params?: any) =>
    api
      .get(`/data-sources/${id}/data`, { params })
      .then((res) => res.data.data),

  deleteDataSource: (id: string) =>
    api.delete(`/data-sources/${id}`).then((res) => res.data),
};

// Charts API
export const chartAPI = {
  getCharts: (dashboardId: string) =>
    api
      .get(`/charts/dashboard/${dashboardId}`)
      .then((res) => res.data.data.charts),

  getChart: (id: string) =>
    api.get(`/charts/${id}`).then((res) => res.data.data.chart),

  createChart: (data: any) =>
    api.post("/charts", data).then((res) => res.data.data.chart),

  updateChart: (id: string, data: any) =>
    api.put(`/charts/${id}`, data).then((res) => res.data.data.chart),

  deleteChart: (id: string) =>
    api.delete(`/charts/${id}`).then((res) => res.data),

  getChartData: (id: string, data: any) =>
    api.post(`/charts/${id}/data`, data).then((res) => res.data.data),

  updateChartPosition: (id: string, data: any) =>
    api.put(`/charts/${id}/position`, data).then((res) => res.data.data.chart),
};

// AI API
export const aiAPI = {
  generateSummary: (data: any) =>
    api.post("/ai/summarize", data).then((res) => res.data.data),

  processQuery: (data: any) =>
    api.post("/ai/query", data).then((res) => res.data.data),

  getInsights: (dashboardId: string) =>
    api
      .get(`/ai/insights/${dashboardId}`)
      .then((res) => res.data.data.insights),

  generateInsights: (dashboardId: string) =>
    api
      .post("/ai/generate-insights", { dashboardId })
      .then((res) => res.data.data),

  deleteInsight: (id: string) =>
    api.delete(`/ai/insights/${id}`).then((res) => res.data),
};

export default api;




